import { EventEmitter } from "events";
import Cookies from "js-cookie";
import ReconnectingWebSocket from "reconnecting-websocket";
import apiFactory from ".";

const eventEmitter = new EventEmitter();
const REACT_APP_AUTHENTICATION_WEB =
  process.env.REACT_APP_AUTHENTICATION_WEB || "http://localhost:4005";

const SOCKET_STATUS = {
  CONNECTING: "CONNECTING",
  CONNECTED: "CONNECTED",
  DISCONNECTED: "DISCONNECTED",
};

const MAX_RETRIES_LIMIT = 5;

let chatSocket = null;
let authSocket = null;
let countReconnectedSuccess = 0;
let currentRetryCount = 0;
let refreshingTokenPromise = null;

const refreshAccessToken = async () => {
  if (!refreshingTokenPromise) {
    refreshingTokenPromise = (async () => {
      try {
        const result = await apiFactory.authApi.getAccessToken({
          refreshToken: Cookies.get("refresh_token"),
        });

        if (result?.status === 400) {
          Cookies.remove("access_token");
          Cookies.remove("refresh_token");
          localStorage.clear();
          window.location.href = REACT_APP_AUTHENTICATION_WEB + "/login";
          return;
        }

        if (result?.status === 200) {
          Cookies.set("access_token", result.data);
          console.log("✅ Token refreshed");
        } else {
          console.warn("❌ Token refresh failed. Redirecting to login...");
        }

        return result;
      } catch (error) {
        console.error("❌ Error while refreshing token:", error);
        return null;
      } finally {
        refreshingTokenPromise = null;
      }
    })();
  }

  return refreshingTokenPromise;
};

const initChatSocket = async () => {
  if (chatSocket && chatSocket.readyState !== WebSocket.CLOSED) {
    return;
  }

  closeChatSocket();

  if (!("WebSocket" in window)) {
    console.error("⛔ Chat Socket is not supported in this browser.");
    return;
  }

  const url = process.env.REACT_APP_WEBSOCKET_URL;
  const access_token = Cookies.get("access_token");

  if (!access_token || !url || access_token?.includes("https://")) {
    console.warn("⚠️ No access token found, WebSocket not initialized.");
    return;
  }

  const socketUrl = `${url}/websocket?Bearer=${access_token}`;

  eventEmitter.emit("socketStatusChange", {
    status: SOCKET_STATUS.CONNECTING,
    retryCount: currentRetryCount,
  });

  chatSocket = new ReconnectingWebSocket(socketUrl, [], {
    maxRetries: 20,
    reconnectInterval: 3000,
    reconnectDecay: 1.5,
  });

  chatSocket.onopen = () => {
    console.log("✅ Chat Socket Connected");

    currentRetryCount = 0;
    countReconnectedSuccess++;

    eventEmitter.emit("socketStatusChange", {
      status: SOCKET_STATUS.CONNECTED,
      retryCount: 0,
    });

    eventEmitter.emit(
      "countReconnectedSuccessUpdated",
      countReconnectedSuccess
    );
  };

  chatSocket.onerror = async (error) => {
    console.error("❌ Chat Socket Error:", error);

    currentRetryCount++;

    eventEmitter.emit("socketStatusChange", {
      status: SOCKET_STATUS.DISCONNECTED,
      retryCount: currentRetryCount,
    });

    const result = await refreshAccessToken();

    if (result?.data) {
      if (chatSocket) chatSocket.close();
      initChatSocket();
    }
  };

  chatSocket.onclose = (event) => {
    if (!event?.wasClean) {
      console.warn(`⚠️ Chat Socket closed unexpectedly: Code ${event.code}`);

      if (
        chatSocket.readyState === WebSocket.CLOSED ||
        chatSocket.readyState === WebSocket.CLOSING
      ) {
        eventEmitter.emit("socketStatusChange", {
          status: SOCKET_STATUS.DISCONNECTED,
          retryCount: currentRetryCount,
        });
      }
    } else {
      console.log("🛑 Chat Socket connection closed cleanly.");
    }
  };
};

const closeChatSocket = () => {
  if (chatSocket) {
    chatSocket.close(1000, "Normal closure: Closing chat WebSocket");
    chatSocket = null;
    currentRetryCount = 0;
  }
};

const initAuthSocket = async () => {
  closeAuthSocket();

  if (!("WebSocket" in window)) {
    console.error("⛔ Auth Socket is not supported in this browser.");
    return;
  }

  const url = process.env.REACT_APP_AUTH_WEBSOCKET_URL;
  const access_token = Cookies.get("access_token");

  if (!access_token || !url || access_token?.includes("https://")) {
    console.warn("⚠️ No access token found, WebSocket not initialized.");
    return;
  }

  const socketUrl = `${url}/websocket?Bearer=${access_token}`;

  authSocket = new ReconnectingWebSocket(socketUrl, [], {
    maxRetries: 5,
    reconnectInterval: 3000,
    reconnectDecay: 2,
  });

  authSocket.onopen = () => {
    console.log("✅ Auth Socket Connected");
  };

  authSocket.onmessage = (event) => {
    try {
      if (typeof event.data === "string") {
        if (event.data === "PING") {
          authSocket.send("PONG");
        }
      }
    } catch (error) {
      console.error(`❌ Error parsing authSocket message:`, error);
    }
  };

  authSocket.onerror = async (error) => {
    console.error("❌ Auth Socket Error:", error);
    const result = await refreshAccessToken();

    if (result?.data) {
      if (authSocket) authSocket.close();
      setTimeout(() => {
        initAuthSocket();
      }, 100);
    }
  };

  authSocket.onclose = (event) => {
    if (!event?.wasClean) {
      console.warn(`⚠️ Auth Socket closed unexpectedly: Code ${event.code}`);
    } else {
      console.log("🛑 Auth Socket connection closed cleanly.");
    }
  };
};

const closeAuthSocket = () => {
  if (authSocket) {
    authSocket.close(1000, "Normal closure: Closing auth WebSocket");
    authSocket = null;
  }
};

export {
  chatSocket,
  closeAuthSocket,
  closeChatSocket,
  countReconnectedSuccess,
  eventEmitter,
  initAuthSocket,
  initChatSocket,
  MAX_RETRIES_LIMIT,
  SOCKET_STATUS,
};
